// Matches Tab Styles
// Styling for single-card Matches tab container

import styled, { keyframes } from "styled-components";
import { theme } from "../theme";
import { FEED_NAV_HEIGHT } from "./CompletenessBanner.styles";

const toastIn = keyframes`
  from { opacity: 0; transform: translate(-50%, -10px); }
  to   { opacity: 1; transform: translate(-50%, 0); }
`;

// Main Container
// Uses absolute positioning to fill parent container
export const MatchesContainer = styled.div<{ $height?: number }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  /* Height is determined by absolute positioning (top: 0, bottom: 0) */
  background: ${theme.colors.background.primary};
  overflow: hidden;
`;

// Header with Status Indicators
export const MatchesHeader = styled.div`
  position: fixed;
  top: 120px;
  left: 0;
  right: 0;
  z-index: 998;
  background: ${theme.colors.background.overlay};
  backdrop-filter: blur(20px);
  border-bottom: 1px solid ${theme.colors.border.light};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: ${theme.breakpoints.mobile}) {
    top: 100px;
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
  }
`;

export const MatchesHeaderStatus = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.lg};
  width: 100%;
  max-width: 500px;
  justify-content: space-around;

  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: ${theme.spacing.md};
  }
`;

export const MatchesStatusItem = styled.button`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.light};
  border-radius: ${theme.borderRadius.full};
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  color: ${theme.colors.text.primary};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);

  &:hover {
    background: ${theme.colors.background.tertiary};
    border-color: ${theme.colors.border.orange};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.8rem;
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
  }
`;

// Scroll Container - One card at a time with snap scrolling
// Positioned to account for top navigation (header + tabs)
export const MatchesScrollContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: scroll;
  overflow-x: hidden;
  scroll-behavior: smooth;
  scroll-snap-type: y mandatory;
  /* Account for bottom navigation: ~70px - prevents last card from being hidden */
  scroll-padding-bottom: 70px;
  scroll-padding-top: 0;
  /* No top padding - cards extend to top, tabs overlay transparently */
  padding-top: 0;
  padding-bottom: 0;
  padding-left: 0;
  padding-right: 0;
  margin: 0;
  width: 100%;
  /* Height is determined by absolute positioning (top: 0, bottom: 0) */
  /* Prevent any gaps between cards */
  gap: 0;
  /* Ensure cards snap perfectly with no gaps */
  scroll-snap-stop: always;
  /* Improve scroll performance */
  will-change: scroll-position;
  transform: translateZ(0);
  /* Ensure scrolling is enabled */
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  /* Allow touch scrolling */
  touch-action: pan-y;

  /* Hide scrollbar */
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    /* Mobile bottom nav: ~70px */
    scroll-padding-bottom: 70px;
    scroll-padding-top: 0;
    padding-top: 0;
    /* Height is determined by absolute positioning (top: 0, bottom: 0) */
  }
  
  /* Ensure children (cards) have no gaps and fill exactly */
  > * {
    margin: 0;
    padding: 0;
    flex-shrink: 0;
    scroll-snap-align: start;
    scroll-snap-stop: always;
  }
`;

// Scroll Hint
export const ScrollHint = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  color: ${theme.colors.text.muted};
  font-size: 0.875rem;
  font-weight: 500;
  animation: bounce 2s infinite;
  pointer-events: none;
  z-index: 5;

  @keyframes bounce {
    0%, 100% {
      transform: translateX(-50%) translateY(0);
    }
    50% {
      transform: translateX(-50%) translateY(-10px);
    }
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.8rem;
    bottom: 1.5rem;
  }
`;

// Loading States
export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${theme.colors.text.secondary};
  gap: ${theme.spacing.md};

  p {
    margin: 0;
    font-size: 0.9rem;
  }
`;

export const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(102, 126, 234, 0.3);
  border-top: 3px solid ${theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

// Empty State
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  color: ${theme.colors.text.secondary};
  padding: ${theme.spacing.xl};
`;

export const EmptyStateIcon = styled.div`
  font-size: 4rem;
  margin-bottom: ${theme.spacing.md};
  opacity: 0.5;
`;

export const EmptyStateTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 ${theme.spacing.sm} 0;
  color: ${theme.colors.text.primary};
`;

export const EmptyStateDescription = styled.p`
  font-size: 1rem;
  margin: 0;
  max-width: 400px;
  line-height: 1.5;
  color: ${theme.colors.text.secondary};
`;


// Transient toast for the Matches feed.
//
// z-index has to clear the ProfilePeekDrawer (10021), because the action that
// most often raises a toast — the Message button — lives inside the open
// drawer. Anything lower renders behind it and is never seen.
export const FeedToast = styled.div`
  position: fixed;
  top: calc(${FEED_NAV_HEIGHT} + ${theme.spacing.sm});
  left: 50%;
  transform: translateX(-50%);
  z-index: 10030;
  max-width: min(90vw, 26rem);
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.full};
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  font-size: 0.85rem;
  font-weight: 500;
  text-align: center;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  animation: ${toastIn} 0.3s ease;

  // Fixed elements ignore the page padding that clears the desktop sidebar,
  // so the centre point needs shifting by half the sidebar width to stay
  // visually centred over the feed (same pattern as FeedNavigation).
  @media (min-width: calc(${theme.breakpoints.tablet} + 1px)) {
    left: calc(50% + 44px);
  }
`;
